import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { TextField } from "@merge-api/merge-javascript-shared";
import { Search } from "lucide-react";

interface Props {
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
}

const DEBOUNCE_MS = 300;

const LinkedAccountsSearchField = ({ value, onChange, disabled }: Props) => {
  const [draft, setDraft] = useState(value ?? "");

  // The debounce effect must not re-run when onChange changes identity, but it does need to
  // call the current one, or it commits against a stale copy of the filters.
  const onChangeRef = useRef(onChange);
  const committedRef = useRef(value ?? "");

  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    const next = value ?? "";
    if (next === committedRef.current) return;
    committedRef.current = next;
    setDraft(next);
  }, [value]);

  // Each keystroke would otherwise refetch the list, so the committed value trails typing.
  useEffect(() => {
    if (draft === committedRef.current) return undefined;

    const timeout = setTimeout(() => {
      committedRef.current = draft;
      onChangeRef.current(draft || null);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [draft]);

  return (
    <TextField
      placeholder="Search by ID, organization, or email"
      value={draft}
      disabled={disabled}
      prefix={
        <div className={clsx("flex h-full items-center rounded-l-md", disabled && "bg-gray-10")}>
          <Search size={16} className="ml-3 text-gray-40" />
        </div>
      }
      prefixVariant="inline"
      inputClassName="pl-2"
      onChange={(event) => setDraft(event.target.value)}
    />
  );
};

export default LinkedAccountsSearchField;
